import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import cx from 'classnames';
import type { CartItemType } from '@zola-helpers/client/dist/es/@types/cart';
import { ConvertWebApiDeclaredToActual } from '@zola-helpers/client/dist/es/@types/svc-web-api';
import type { WProjectCustomizationFullView, WProjectFullView } from '@zola/svc-web-api-ts-client';
import _debounce from 'lodash/debounce';
import { LeadCardType } from '../../../../paper/cards/constants/cardTypes';
import { CardSubItem } from './CardSubItem';
import DateRange from '../DateRange';
import { useCartContext } from '../../CartContext';
import { CardItemImage } from './CardItemImage';
import { LinkV2 } from '../../../LinkV2';
import { getCardSuitePath } from '../../../../paper/cards/util/getCardUrl';
import { getCustomizationStepUrl } from '../../../../paper/cards/util/getCustomizationStepUrl';
import { CardItemAttributeList } from './CardItemAttributes';
import { CartItemPrice } from '../CartItemPrice';
import { useForceUpdateOnWindowResize } from '../../../../hooks/useForceUpdateOnWindowResize';

import { CartItemDetails } from '../CartItemDetails';
import { CartItemNamePriceWrapper } from '../CartItemName';
import { CartItemQuantity } from '../CartItemQuantity';
import { RemoveFromCartButton } from '../RemoveFromCartButton';
import { CartItemImage } from '../CartItemImage';
import { CartItemSubTotal } from '../CartItemSubTotal';
import { CartItemDiscount } from '../CartItemDiscount';

import styles from './cardItem.module.less';
import mixinStyles from '../../stylesheets/mixins.module.less';
import cartContainerStyles from '../../stylesheets/cartContainer.module.less';
import attributeStyles from '../cartItemAttributes.module.less';
import { ItemInCartImage } from '../CartItem';

type CartInvitesProps = {
  item: CartItemType & {
    // These don't exist on the CartItemType?  MAybe something else is adding them in?
    exclusionFlag?: boolean;
    exclusionMessage?: string;
  };
};

export const useCanEditProject = ({
  project,
}: {
  project: ConvertWebApiDeclaredToActual<WProjectFullView> | null;
}) => {
  useForceUpdateOnWindowResize();

  const [canEdit, setCanEdit] = useState(project?.editable && project?.customizable);

  const determineCanEdit = useMemo(
    () =>
      _debounce(() => {
        setCanEdit(project?.editable && project.customizable);
      }, 500),
    [project?.editable, project?.customizable]
  );

  useEffect(() => {
    determineCanEdit();

    window.addEventListener('resize', determineCanEdit);
    return () => {
      window.removeEventListener('resize', determineCanEdit);
    };
  }, [determineCanEdit]);

  return Boolean(canEdit);
};

const CustomizationLink: React.FC<{
  className?: string;
  project: ConvertWebApiDeclaredToActual<WProjectFullView> | null;
  customization: ConvertWebApiDeclaredToActual<WProjectCustomizationFullView> | null;
  renderWhenNotCustomizable?: boolean;
}> = ({ children, className, project, customization, renderWhenNotCustomizable = false }) => {
  const canEdit = useCanEditProject({ project });

  const getCustomizationPath = useCallback(() => {
    if (project?.uuid && project?.seeded_card_suite_uuid && customization?.type) {
      if (!canEdit) {
        return getCardSuitePath(customization.type as LeadCardType, project.seeded_card_suite_uuid);
      }
      return getCustomizationStepUrl(project?.uuid, undefined, undefined);
    }
    return undefined;
  }, [canEdit, customization?.type, project?.seeded_card_suite_uuid, project?.uuid]);

  if (canEdit || renderWhenNotCustomizable) {
    return (
      <LinkV2
        href={getCustomizationPath() || '#'}
        className={cx(styles.editButton, className)}
        noTextTransform
      >
        {children}
      </LinkV2>
    );
  }
  return <Fragment />;
};

export const CardItem = ({ item }: CartInvitesProps) => {
  const { removeCartItems, getCardProject, getCardCustomization } = useCartContext();

  const { childItems } = item;

  const singleItem = childItems?.length === 1;

  const removeFromCart = useCallback(() => {
    return removeCartItems(item.suiteProductIds || []);
  }, [item.suiteProductIds, removeCartItems]);

  const cardProject = useMemo(
    () => getCardProject(item.projectUuid as string),
    [getCardProject, item.projectUuid]
  );

  const customization = useMemo(() => {
    return getCardCustomization(item.projectUuid as string, item.customizationUuid as string);
  }, [getCardCustomization, item.customizationUuid, item.projectUuid]);

  const attributes = useMemo(() => {
    let itemAttributes = [];

    if (item.availability?.arrivalRange) {
      itemAttributes.push({
        label: 'Estimated arrival',
        value: (
          <DateRange
            dateRange={item.availability.arrivalRange}
            className={cx(attributeStyles.value, attributeStyles.noTextTransform)}
          />
        ),
      });
    }

    if (singleItem) {
      // Move the attributes up from a single child
      itemAttributes = [...itemAttributes, ...(childItems?.[0].attributes || [])];
    }
    return itemAttributes;
  }, [childItems, item.availability?.arrivalRange, singleItem]);

  const canEdit = useCanEditProject({ project: cardProject });

  const renderableChildren = useMemo(() => {
    return singleItem ? [] : childItems || [];
  }, [childItems, singleItem]);

  const cardImageClassName = useMemo(() => {
    const numChildren = renderableChildren.length;
    switch (numChildren) {
      case 0:
        return styles.cardImageSpan1;
      // We need to span the parent plus the children but the parent element is
      // also included in the children as the first item, so we have 1 more span
      // here than you might expect.  IE, a card with 2 items (invite + envelope)
      // takes up 3 rows in the grid (parent with the image, invite, and envelope)

      case 1:
        // This is just here for safety, we shouldn't be rendering a single child
        // Instead we just render the parent and promote attributes up.
        return styles.cardImageSpan2;
      case 2:
        return styles.cardImageSpan3;
      case 3:
        return styles.cardImageSpan4;
      default:
        return styles.cardImageSpan5;
    }
  }, [renderableChildren.length]);

  return (
    <Fragment>
      {customization ? (
        <CartItemImage className={cardImageClassName}>
          {childItems?.[0] && (
            <CustomizationLink
              project={cardProject}
              customization={customization}
              renderWhenNotCustomizable
            >
              <CardItemImage className={styles.cardImage} item={childItems?.[0]} />
            </CustomizationLink>
          )}
        </CartItemImage>
      ) : (
        <ItemInCartImage item={item} />
      )}
      <div className={styles.cardItemPushBottom}>
        <CartItemDetails>
          <CartItemNamePriceWrapper>
            <CustomizationLink
              project={cardProject}
              customization={customization}
              renderWhenNotCustomizable
            >
              {item.name}
            </CustomizationLink>
            <CartItemSubTotal className={cartContainerStyles.visibleContainerSmall} item={item} />
          </CartItemNamePriceWrapper>

          {attributes.length > 0 && <CardItemAttributeList attributes={attributes} />}

          {item.availability?.isExpeditable && (
            <CartItemDiscount>Two-day shipping available</CartItemDiscount>
          )}

          {item.cartItemMessage && <CartItemDiscount>{item.cartItemMessage}</CartItemDiscount>}
        </CartItemDetails>

        {canEdit && (
          <div className={cartContainerStyles.hiddenContainerSmall}>
            <CustomizationLink project={cardProject} customization={customization}>
              Edit customization
            </CustomizationLink>
          </div>
        )}
      </div>

      {singleItem ? (
        <CartItemPrice item={item} />
      ) : (
        <div className={cartContainerStyles.hiddenContainerSmall} />
      )}

      <CartItemQuantity>
        {singleItem && <div>{childItems?.[0].contribution?.quantity}</div>}
        <RemoveFromCartButton removeFromCart={removeFromCart} />
      </CartItemQuantity>

      <CartItemSubTotal
        item={item}
        className={cx(cartContainerStyles.hiddenContainerSmall, mixinStyles.alignRight)}
      />

      {!singleItem && (
        <Fragment>
          {renderableChildren?.map((childItem) => (
            <Fragment key={childItem.id}>
              <div className={cx(styles.subDivider, styles.withEnhancedPaper)} />
              <CardSubItem item={childItem} customization={customization} />
            </Fragment>
          ))}
        </Fragment>
      )}

      <div className={cx(cartContainerStyles.visibleContainerSmall, styles.smallContainerControls)}>
        <CustomizationLink project={cardProject} customization={customization}>
          Edit customization
        </CustomizationLink>

        <RemoveFromCartButton removeFromCart={removeFromCart} />
      </div>
    </Fragment>
  );
};
